/**
 * Admin -> Edge Fleet -> Device groups and scan hours (release 3.3, R3-OI-194).
 *
 * Scan hours used to be set one device at a time. For a fleet of thousands of
 * laptops the operator now sets them for the whole tenant, for a business unit,
 * or for a group of devices, and sees for every device which hours are in force
 * and where they come from. Highest first: the device's own setting, its group
 * (the lowest priority number wins), its business unit, the tenant, the hours
 * baked in at install, any time.
 *
 * Groups are static (devices added by the Edge Fleet filter, thousands in one
 * act, with the count confirmed) or rule-based (OS, type, hostname, address,
 * version). An enrolment token can carry a group (Provision pod), so a laptop
 * has the group's hours before its first scan. "Scan now" asks every device of
 * a group to scan, the start spread over up to four hours.
 *
 * Every change needs a reason and is audited. A controller before 3.3 answers
 * 404 and the panel says so.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Play, Plus, RefreshCw, Save, Trash2, Users, X } from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ApiError,
  addDeviceGroupMembers,
  createDeviceGroup,
  deleteDeviceGroup,
  getFleetPolicy,
  getFleetPolicySummary,
  listBusinessUnits,
  listDeviceGroupMembers,
  listDeviceGroups,
  previewDeviceGroupMembers,
  problemDetail,
  removeDeviceGroupMembers,
  scanDeviceGroupNow,
  setFleetPolicy,
  updateDeviceGroup,
  type BusinessUnit,
  type DeviceGroup,
  type DeviceGroupFilter,
  type DeviceGroupRule,
  type FleetPolicyRow,
  type FleetPolicySummary,
  type PolicyDeviceRow,
} from '@/lib/api';

import { BranchLinkEditor, branchLinkShort } from './BranchLinkSettings';
import { describeScanWindow, scanWindowFromInputs } from './EdgePodScanHours';
import { count, ruleText, shortWindow, SOURCE_ORDER, SOURCE_TITLE, sourceText } from './scan-policy-format';

const INPUT = 'glass-lite rounded px-2 py-1 bg-background/40 border border-border/40 text-xs';
const SPREADS = [0, 15, 30, 60, 120, 240] as const;
/** The controller refuses "all at once" above this many devices (device-groups.ts SCAN_NOW_MAX_UNSPREAD). */
const MAX_UNSPREAD = 500;

/** From/Until, or both empty for "no hours at this level". */
function windowOrNull(start: string, end: string): { window: string | null } | { error: string } {
  if (!start && !end) return { window: null };
  return scanWindowFromInputs(start, end);
}

function HoursInputs(props: {
  start: string;
  end: string;
  onStart: (v: string) => void;
  onEnd: (v: string) => void;
  label: string;
}): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1">
      <input type="time" aria-label={`${props.label} from`} value={props.start} onChange={(e) => props.onStart(e.target.value)} className={INPUT} />
      <span className="text-muted-foreground">to</span>
      <input type="time" aria-label={`${props.label} until`} value={props.end} onChange={(e) => props.onEnd(e.target.value)} className={INPUT} />
    </span>
  );
}

export function DeviceGroupsPanel(): JSX.Element {
  const [groups, setGroups] = useState<DeviceGroup[] | null>(null);
  const [policy, setPolicy] = useState<{ tenant: FleetPolicyRow | null; businessUnits: FleetPolicyRow[] } | null>(null);
  const [summary, setSummary] = useState<FleetPolicySummary | null>(null);
  const [units, setUnits] = useState<BusinessUnit[]>([]);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [g, p, s] = await Promise.all([listDeviceGroups(), getFleetPolicy(), getFleetPolicySummary()]);
      setGroups(g.groups);
      setPolicy(p);
      setSummary(s);
      setUnavailable(false);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) setUnavailable(true);
      else setError(problemDetail(e, 'could not load device groups'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    listBusinessUnits({ status: 'active' })
      .then((r) => setUnits(r.businessUnits))
      .catch(() => setUnits([]));
  }, [load]);

  const unitName = useCallback((id: string | null) => units.find((u) => u.id === id)?.name ?? null, [units]);
  const selectedGroup = useMemo(() => groups?.find((g) => g.id === selected) ?? null, [groups, selected]);

  return (
    <GlassCard className="mt-4 p-0" data-testid="device-groups-panel">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Device groups and scan hours
          </CardTitle>
          <CardDescription>
            Set the hours scheduled scans may run in for the whole tenant, a business unit or a group of
            devices. Highest first: a device&apos;s own setting, its group, its business unit, the tenant,
            the hours baked in at install. Delivered on each device&apos;s next check-in.
          </CardDescription>
        </div>
        <Button variant="ghost" size="sm" onClick={() => void load()} aria-label="Reload device groups">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>

      <div className="space-y-5 px-6 pb-6">
        {loading && !groups && !unavailable && !error ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading...
          </p>
        ) : null}
        {unavailable ? (
          <p className="text-sm text-muted-foreground" data-testid="device-groups-unavailable">
            This controller does not offer device groups (it predates release 3.3).
          </p>
        ) : null}
        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}

        {summary ? <SummaryTiles summary={summary} /> : null}
        {policy ? <FleetPolicySection policy={policy} units={units} onSaved={load} /> : null}
        {policy ? <BranchLinkPolicySection policy={policy} units={units} onSaved={load} /> : null}

        {groups ? (
          <section className="space-y-2" aria-label="Device groups">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-medium text-foreground">Groups</h3>
              <Button size="sm" variant={creating ? 'ghost' : 'default'} onClick={() => setCreating((v) => !v)}>
                {creating ? <X className="mr-1 h-3 w-3" /> : <Plus className="mr-1 h-3 w-3" />}
                {creating ? 'Cancel' : 'New group'}
              </Button>
            </div>
            {creating ? (
              <CreateGroupForm
                units={units}
                onCreated={async (g) => {
                  setCreating(false);
                  await load();
                  setSelected(g.id);
                }}
              />
            ) : null}
            {groups.length === 0 ? (
              <p className="glass-lite rounded p-4 text-center text-xs text-muted-foreground">
                No groups yet. Without one, devices take their business unit&apos;s or the tenant&apos;s hours.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="device-groups-table">
                  <thead>
                    <tr className="text-left text-muted-foreground">
                      <th className="py-1.5 pr-3 font-normal">Priority</th>
                      <th className="py-1.5 pr-3 font-normal">Group</th>
                      <th className="py-1.5 pr-3 font-normal">Business unit</th>
                      <th className="py-1.5 pr-3 font-normal">Scan hours</th>
                      <th className="py-1.5 pr-3 font-normal">Branch link</th>
                      <th className="py-1.5 pr-3 text-right font-normal">Devices</th>
                      <th className="py-1.5 pr-3 text-right font-normal">Enrolment tokens</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((g) => (
                      <tr
                        key={g.id}
                        tabIndex={0}
                        aria-selected={selected === g.id}
                        aria-label={`Open the group ${g.name}`}
                        className={`cursor-pointer border-t border-border/30 hover:bg-primary/5 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary ${selected === g.id ? 'bg-primary/10' : ''}`}
                        onClick={() => setSelected(selected === g.id ? null : g.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelected(selected === g.id ? null : g.id);
                          }
                        }}
                      >
                        <td className="py-2 pr-3 tabular-nums">{g.priority}</td>
                        <td className="py-2 pr-3">
                          <div className="flex items-center gap-1.5">
                            <span className="text-foreground">{g.name}</span>
                            <Badge variant={g.kind === 'rule' ? 'info' : 'secondary'}>{g.kind === 'rule' ? 'rule' : 'static'}</Badge>
                          </div>
                          {g.kind === 'rule' ? <div className="text-[11px] text-muted-foreground">{ruleText(g.rule)}</div> : null}
                        </td>
                        <td className="py-2 pr-3">{g.businessUnitName ?? 'Whole tenant'}</td>
                        <td className="whitespace-nowrap py-2 pr-3 tabular-nums">{shortWindow(g.scanWindow)}</td>
                        <td className="py-2 pr-3 text-muted-foreground">{branchLinkShort(g)}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{count(g.memberCount)}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{count(g.tokenCount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        ) : null}

        {selectedGroup ? (
          <GroupDetail
            key={selectedGroup.id}
            group={selectedGroup}
            units={units}
            unitName={unitName}
            onChanged={load}
            onDeleted={async () => {
              setSelected(null);
              await load();
            }}
          />
        ) : null}
      </div>
    </GlassCard>
  );
}

function SummaryTiles({ summary }: { summary: FleetPolicySummary }): JSX.Element {
  return (
    <section className="space-y-2" aria-label="Where scan hours come from">
      <h3 className="text-sm font-medium text-foreground">
        Where the hours of {count(summary.total)} device{summary.total === 1 ? '' : 's'} come from
      </h3>
      <dl className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-3 lg:grid-cols-6" data-testid="device-groups-summary">
        {SOURCE_ORDER.map((s) => (
          <div key={s} className="glass-lite rounded p-2.5">
            <dt className="text-muted-foreground">{SOURCE_TITLE[s]}</dt>
            <dd className="text-base tabular-nums text-foreground">{count(summary.bySource[s] ?? 0)}</dd>
          </div>
        ))}
      </dl>
      {summary.byGroup.length > 0 ? (
        <p className="text-[11px] text-muted-foreground">
          By group:{' '}
          {summary.byGroup
            .slice(0, 5)
            .map((g) => `${g.groupName} ${count(g.devices)}`)
            .join(', ')}
          {summary.byGroup.length > 5 ? `, and ${summary.byGroup.length - 5} more` : ''}
        </p>
      ) : null}
    </section>
  );
}

function FleetPolicySection(props: {
  policy: { tenant: FleetPolicyRow | null; businessUnits: FleetPolicyRow[] };
  units: BusinessUnit[];
  onSaved: () => Promise<void>;
}): JSX.Element {
  const [scope, setScope] = useState<string>('tenant');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const rows: Array<{ key: string; label: string; row: FleetPolicyRow | null }> = [
    { key: 'tenant', label: 'Whole tenant', row: props.policy.tenant },
    ...props.policy.businessUnits.map((r) => ({ key: r.businessUnitId ?? '', label: r.businessUnitName ?? 'Business unit', row: r })),
  ];

  async function save(clear: boolean): Promise<void> {
    // Save needs both times; only Clear removes the hours (review H2: an empty
    // Save used to clear them for the whole tenant).
    const w = clear ? { window: null } : scanWindowFromInputs(start, end);
    if ('error' in w) return setMsg(w.error);
    if (reason.trim().length < 3) return setMsg('Give a reason (it is kept in the audit log).');
    const label = scope === 'tenant' ? 'the whole tenant' : (props.units.find((u) => u.id === scope)?.name ?? 'this unit');
    if (
      clear &&
      !window.confirm(`Clear the scan hours of ${label}? Its devices fall back to the next level (the tenant, or the hours baked in at install).`)
    ) {
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await setFleetPolicy({
        scopeType: scope === 'tenant' ? 'tenant' : 'business_unit',
        ...(scope === 'tenant' ? {} : { businessUnitId: scope }),
        scanWindow: w.window,
        reason: reason.trim(),
      });
      setStart('');
      setEnd('');
      setReason('');
      await props.onSaved();
    } catch (e) {
      setMsg(problemDetail(e, 'could not save the hours'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="space-y-2" aria-label="Tenant and business unit hours">
      <h3 className="text-sm font-medium text-foreground">Tenant and business unit hours</h3>
      <table className="w-full text-xs" data-testid="fleet-policy-table">
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-t border-border/30">
              <td className="py-1.5 pr-3 text-foreground">{r.label}</td>
              <td className="py-1.5 pr-3">{r.row?.scanWindow ? describeScanWindow(r.row.scanWindow) : 'Not set'}</td>
              <td className="py-1.5 text-right text-muted-foreground">
                {r.row ? `changed ${new Date(r.row.updatedAt).toLocaleDateString('en-IN')}` : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <select aria-label="Hours for" value={scope} onChange={(e) => setScope(e.target.value)} className={INPUT}>
          <option value="tenant">Whole tenant</option>
          {props.units.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
        <HoursInputs start={start} end={end} onStart={setStart} onEnd={setEnd} label="Policy hours" />
        <input
          aria-label="Reason for the change"
          placeholder="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className={`${INPUT} min-w-[12rem] flex-1`}
        />
        <Button size="sm" disabled={busy} onClick={() => void save(false)}>
          {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Save className="mr-1 h-3 w-3" />}
          Save hours
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => void save(true)}>
          Clear
        </Button>
      </div>
      {msg ? (
        <p role="alert" className="text-xs text-destructive">
          {msg}
        </p>
      ) : null}
    </section>
  );
}

/**
 * Release 3.3 (R3-OI-194): the branch-link settings of the tenant and each
 * business unit: check-in profile, check-in quiet hours, upload limit.
 */
function BranchLinkPolicySection(props: {
  policy: { tenant: FleetPolicyRow | null; businessUnits: FleetPolicyRow[] };
  units: BusinessUnit[];
  onSaved: () => Promise<void>;
}): JSX.Element {
  const [scope, setScope] = useState<string>('tenant');
  const [reason, setReason] = useState('');
  const rows: Array<{ key: string; label: string; row: FleetPolicyRow | null }> = [
    { key: 'tenant', label: 'Whole tenant', row: props.policy.tenant },
    ...props.policy.businessUnits.map((r) => ({ key: r.businessUnitId ?? '', label: r.businessUnitName ?? 'Business unit', row: r })),
  ];
  const current =
    scope === 'tenant' ? props.policy.tenant : (props.policy.businessUnits.find((r) => r.businessUnitId === scope) ?? null);
  const label = scope === 'tenant' ? 'the whole tenant' : (props.units.find((u) => u.id === scope)?.name ?? 'this unit');

  return (
    <section className="space-y-2" aria-label="Branch-link settings" data-testid="branch-link-section">
      <div>
        <h3 className="text-sm font-medium text-foreground">Branch links: check-in and upload</h3>
        <p className="text-xs text-muted-foreground">
          For laptops on narrow branch links. Low chatter checks in every 30 minutes instead of 5; inside quiet hours a
          laptop only keeps alive, once an hour; the upload limit paces findings uploads (what waits is sent later, never
          dropped). Uploads are compressed wherever the network path allows it. A group&apos;s setting beats its business
          unit&apos;s, which beats the tenant&apos;s.
        </p>
      </div>
      <table className="w-full text-xs" data-testid="branch-link-table">
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-t border-border/30">
              <td className="py-1.5 pr-3 text-foreground">{r.label}</td>
              <td className="py-1.5 pr-3">{r.row ? branchLinkShort(r.row) : 'not set'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <select aria-label="Branch-link settings for" value={scope} onChange={(e) => setScope(e.target.value)} className={INPUT}>
          <option value="tenant">Whole tenant</option>
          {props.units.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
        <input
          aria-label="Reason for the branch-link change"
          placeholder="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className={`${INPUT} min-w-[12rem] flex-1`}
        />
      </div>
      <BranchLinkEditor
        label={label}
        current={current}
        reason={reason}
        testId="branch-link-policy-editor"
        onSave={async (value) => {
          try {
            await setFleetPolicy({
              scopeType: scope === 'tenant' ? 'tenant' : 'business_unit',
              ...(scope === 'tenant' ? {} : { businessUnitId: scope }),
              ...value,
              reason: reason.trim(),
            });
          } catch (e) {
            throw new Error(problemDetail(e, 'could not save the settings'));
          }
          setReason('');
          await props.onSaved();
        }}
      />
    </section>
  );
}

function CreateGroupForm(props: { units: BusinessUnit[]; onCreated: (g: DeviceGroup) => Promise<void> }): JSX.Element {
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'static' | 'rule'>('static');
  const [rule, setRule] = useState<DeviceGroupRule>({});
  const [unit, setUnit] = useState('');
  const [priority, setPriority] = useState('100');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function submit(): Promise<void> {
    const w = windowOrNull(start, end);
    if ('error' in w) return setMsg(w.error);
    if (!name.trim()) return setMsg('Give the group a name.');
    if (reason.trim().length < 3) return setMsg('Give a reason (it is kept in the audit log).');
    const p = Number(priority);
    if (!Number.isInteger(p) || p < 1 || p > 1000) return setMsg('Priority is a whole number from 1 to 1000.');
    setBusy(true);
    setMsg(null);
    try {
      const r = await createDeviceGroup({
        name: name.trim(),
        kind,
        ...(kind === 'rule' ? { rule } : {}),
        ...(unit ? { businessUnitId: unit } : {}),
        priority: p,
        scanWindow: w.window,
        reason: reason.trim(),
      });
      await props.onCreated(r.group);
    } catch (e) {
      setMsg(problemDetail(e, 'could not create the group'));
    } finally {
      setBusy(false);
    }
  }

  const ruleField = (k: keyof DeviceGroupRule, label: string, placeholder: string) => (
    <label className="flex flex-col gap-0.5">
      <span className="text-muted-foreground">{label}</span>
      <input
        aria-label={`Rule ${label}`}
        placeholder={placeholder}
        value={rule[k] ?? ''}
        onChange={(e) => setRule({ ...rule, [k]: e.target.value })}
        className={INPUT}
      />
    </label>
  );

  return (
    <div className="glass-lite space-y-3 rounded p-3 text-xs" data-testid="device-group-create">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-0.5">
          <span className="text-muted-foreground">Name</span>
          <input aria-label="Group name" value={name} onChange={(e) => setName(e.target.value)} className={INPUT} />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-muted-foreground">Members</span>
          <select aria-label="Group type" value={kind} onChange={(e) => setKind(e.target.value as 'static' | 'rule')} className={INPUT}>
            <option value="static">Chosen devices (static)</option>
            <option value="rule">Devices matching a rule</option>
          </select>
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-muted-foreground">Business unit</span>
          <select aria-label="Group business unit" value={unit} onChange={(e) => setUnit(e.target.value)} className={INPUT}>
            <option value="">Whole tenant</option>
            {props.units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-muted-foreground">Priority</span>
          <input aria-label="Group priority" type="number" min={1} max={1000} value={priority} onChange={(e) => setPriority(e.target.value)} className={`${INPUT} w-20`} />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-muted-foreground">Scan hours (empty: none at this level)</span>
          <HoursInputs start={start} end={end} onStart={setStart} onEnd={setEnd} label="Group hours" />
        </label>
      </div>
      {kind === 'rule' ? (
        <div className="flex flex-wrap items-end gap-3">
          {ruleField('os', 'OS', 'windows')}
          {ruleField('hostnameContains', 'Hostname contains', 'fin-')}
          {ruleField('ipPrefix', 'Address starts with', '10.20.')}
          {ruleField('version', 'Agent version', '0.0.17')}
          <span className="text-[11px] text-muted-foreground">Every condition given must match.</span>
        </div>
      ) : null}
      <p className="text-[11px] text-muted-foreground">
        A group bound to a business unit applies only to devices in that unit. When a device is in more than one
        group that sets hours, the lowest priority number wins.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input
          aria-label="Reason for the new group"
          placeholder="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className={`${INPUT} min-w-[14rem] flex-1`}
        />
        <Button size="sm" disabled={busy} onClick={() => void submit()}>
          {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Plus className="mr-1 h-3 w-3" />}
          Create group
        </Button>
      </div>
      {msg ? (
        <p role="alert" className="text-destructive">
          {msg}
        </p>
      ) : null}
    </div>
  );
}

function GroupDetail(props: {
  group: DeviceGroup;
  units: BusinessUnit[];
  unitName: (id: string | null) => string | null;
  onChanged: () => Promise<void>;
  onDeleted: () => Promise<void>;
}): JSX.Element {
  const g = props.group;
  const [members, setMembers] = useState<PolicyDeviceRow[] | null>(null);
  const [more, setMore] = useState<string | null>(null);
  const [filter, setFilter] = useState<DeviceGroupFilter>({});
  const [preview, setPreview] = useState<{ matching: number; alreadyMembers: number } | null>(null);
  const [reason, setReason] = useState('');
  const [spread, setSpread] = useState<number>(60);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const loadMembers = useCallback(async () => {
    try {
      const r = await listDeviceGroupMembers(g.id, { limit: 50 });
      setMembers(r.devices);
      setMore(r.nextCursor);
    } catch (e) {
      setMsg({ ok: false, text: problemDetail(e, 'could not load the members') });
    }
  }, [g.id]);

  useEffect(() => {
    void loadMembers();
  }, [loadMembers]);

  const loadMore = async () => {
    if (!more) return;
    try {
      const r = await listDeviceGroupMembers(g.id, { limit: 50, cursor: more });
      setMembers((prev) => [...(prev ?? []), ...r.devices]);
      setMore(r.nextCursor);
    } catch (e) {
      setMsg({ ok: false, text: problemDetail(e, 'could not load more members') });
    }
  };

  const cleanFilter = (): DeviceGroupFilter =>
    Object.fromEntries(Object.entries(filter).filter(([, v]) => typeof v === 'string' && v.trim() !== '')) as DeviceGroupFilter;
  const needReason = (): boolean => {
    if (reason.trim().length >= 3) return false;
    setMsg({ ok: false, text: 'Give a reason (it is kept in the audit log).' });
    return true;
  };

  async function act(name: string, fn: () => Promise<string>): Promise<void> {
    setBusy(name);
    setMsg(null);
    try {
      const text = await fn();
      setMsg({ ok: true, text });
      await Promise.all([props.onChanged(), loadMembers()]);
    } catch (e) {
      setMsg({ ok: false, text: problemDetail(e, `could not ${name}`) });
    } finally {
      setBusy(null);
    }
  }

  const countMatches = () =>
    act('count the devices', async () => {
      const p = await previewDeviceGroupMembers(g.id, cleanFilter());
      setPreview(p);
      return `${count(p.matching)} device${p.matching === 1 ? '' : 's'} match; ${count(p.alreadyMembers)} of them already added.`;
    });

  const add = () => {
    if (!preview || needReason()) return;
    void act('add the devices', async () => {
      const r = await addDeviceGroupMembers(g.id, { filter: cleanFilter(), expectedCount: preview.matching, reason: reason.trim() });
      setPreview(null);
      return `Added ${count(r.added)} devices (${count(r.matched)} matched; the others were already added).`;
    });
  };

  const remove = () => {
    if (!preview || needReason()) return;
    void act('remove the devices', async () => {
      const r = await removeDeviceGroupMembers(g.id, { filter: cleanFilter(), expectedCount: preview.alreadyMembers, reason: reason.trim() });
      setPreview(null);
      return `Removed ${count(r.removed)} added devices from the group. Devices in it by enrolment token or rule stay.`;
    });
  };

  const saveHours = (clear: boolean) => {
    const w = clear ? { window: null } : scanWindowFromInputs(start, end);
    if ('error' in w) return setMsg({ ok: false, text: w.error });
    if (needReason()) return;
    if (clear && !window.confirm(`Clear the hours of "${g.name}"? Its ${count(g.memberCount)} devices fall back to their unit's or the tenant's hours.`)) return;
    void act('save the hours', async () => {
      await updateDeviceGroup(g.id, { scanWindow: w.window, reason: reason.trim() });
      setStart('');
      setEnd('');
      return w.window ? `The group's hours are now ${w.window}.` : "The group's hours are cleared.";
    });
  };

  const scanNow = () => {
    if (needReason()) return;
    const over = spread === 0 ? 'all at once' : `spread over ${spread} minutes`;
    if (
      !window.confirm(
        `Ask the ${count(g.memberCount)} devices of "${g.name}" to scan now, ${over}? A scan asked for this way does not wait for the devices' scan hours.`,
      )
    ) {
      return;
    }
    void act('queue the scans', async () => {
      const r = await scanDeviceGroupNow(g.id, { spreadMinutes: spread, reason: reason.trim() });
      const by = r.lastStartBy ? `, the last starting by ${new Date(r.lastStartBy).toLocaleTimeString('en-IN')}` : '';
      return `Scan queued for ${count(r.queued)} devices${by}. ${count(r.alreadyQueued)} already had one waiting; ${count(r.notSupported)} cannot scan on request.`;
    });
  };

  const confirmDelete = () => {
    if (needReason()) return;
    if (!window.confirm(`Delete the group "${g.name}"? Its devices fall back to their unit's or the tenant's hours.`)) return;
    void (async () => {
      setBusy('delete');
      try {
        await deleteDeviceGroup(g.id, reason.trim());
        await props.onDeleted();
      } catch (e) {
        setMsg({ ok: false, text: problemDetail(e, 'could not delete the group') });
        setBusy(null);
      }
    })();
  };

  const field = (k: keyof DeviceGroupFilter, label: string, placeholder: string) => (
    <input
      aria-label={`Filter ${label}`}
      placeholder={placeholder}
      value={filter[k] ?? ''}
      onChange={(e) => {
        setFilter({ ...filter, [k]: e.target.value });
        setPreview(null);
      }}
      className={`${INPUT} w-36`}
    />
  );

  return (
    <section className="glass-lite space-y-3 rounded p-4 text-xs" data-testid="device-group-detail" aria-label={`Group ${g.name}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-medium text-foreground">{g.name}</h3>
          <p className="text-muted-foreground">
            {g.kind === 'rule' ? `Devices matching: ${ruleText(g.rule)}` : 'Devices chosen by hand or by filter'}
            {' - '}
            {g.businessUnitName ? `only in ${g.businessUnitName}` : 'whole tenant'}
            {' - '}priority {g.priority}
            {' - '}
            {g.scanWindow ? describeScanWindow(g.scanWindow) : 'sets no hours'}
          </p>
        </div>
        {!g.editable ? <Badge variant="outline">read only: outside your business units</Badge> : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          aria-label="Reason for the change"
          placeholder="Reason (required for every change)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className={`${INPUT} min-w-[16rem] flex-1`}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground">Group hours</span>
        <HoursInputs start={start} end={end} onStart={setStart} onEnd={setEnd} label="New group hours" />
        <Button size="sm" disabled={!g.editable || busy !== null} onClick={() => saveHours(false)}>
          <Save className="mr-1 h-3 w-3" /> Save hours
        </Button>
        <Button size="sm" variant="ghost" disabled={!g.editable || busy !== null || !g.scanWindow} onClick={() => saveHours(true)}>
          Clear hours
        </Button>
      </div>

      <div className="space-y-1">
        <span className="text-muted-foreground">Branch-link settings ({branchLinkShort(g)})</span>
        <BranchLinkEditor
          label={`the group ${g.name}`}
          current={g}
          disabled={!g.editable || busy !== null}
          reason={reason}
          testId="branch-link-group-editor"
          onSave={async (value) => {
            try {
              await updateDeviceGroup(g.id, { ...value, reason: reason.trim() });
            } catch (e) {
              throw new Error(problemDetail(e, 'could not save the settings'));
            }
            await props.onChanged();
          }}
        />
      </div>

      {g.kind === 'static' ? (
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-muted-foreground">Devices by filter</span>
            {field('q', 'search', 'Name, hostname, user')}
            {field('os', 'OS', 'windows')}
            {field('version', 'version', '0.0.17')}
            <select
              aria-label="Filter business unit"
              value={filter.businessUnitId ?? ''}
              onChange={(e) => {
                setFilter({ ...filter, businessUnitId: e.target.value });
                setPreview(null);
              }}
              className={INPUT}
            >
              <option value="">Any business unit</option>
              {props.units.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
            <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => void countMatches()}>
              Count
            </Button>
          </div>
          {preview ? (
            <div className="flex flex-wrap items-center gap-2" data-testid="device-group-preview">
              <span className="text-foreground">
                {count(preview.matching)} match, {count(preview.alreadyMembers)} of them already added
              </span>
              <Button
                size="sm"
                disabled={!g.editable || busy !== null || preview.matching - preview.alreadyMembers <= 0}
                onClick={add}
              >
                Add {count(Math.max(0, preview.matching - preview.alreadyMembers))} devices
              </Button>
              <Button size="sm" variant="ghost" disabled={!g.editable || busy !== null || preview.alreadyMembers === 0} onClick={remove}>
                Remove the {count(preview.alreadyMembers)} added
              </Button>
              <span className="text-[11px] text-muted-foreground">
                Devices in the group by their enrolment token or a rule stay in it.
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground">Scan every device now, starts spread over</span>
        <select aria-label="Spread the start over" value={spread} onChange={(e) => setSpread(Number(e.target.value))} className={INPUT}>
          {SPREADS.map((m) => (
            <option key={m} value={m} disabled={m === 0 && g.memberCount > MAX_UNSPREAD}>
              {m === 0 ? 'no spread (all at once)' : m < 60 ? `${m} minutes` : `${m / 60} hour${m === 60 ? '' : 's'}`}
            </option>
          ))}
        </select>
        <Button size="sm" variant="outline" disabled={!g.editable || busy !== null} onClick={scanNow}>
          <Play className="mr-1 h-3 w-3" /> Scan now
        </Button>
        <span className="text-[11px] text-muted-foreground">Does not wait for scan hours.</span>
        <span className="flex-1" />
        <Button size="sm" variant="ghost" className="text-destructive" disabled={!g.editable || busy !== null} onClick={confirmDelete}>
          <Trash2 className="mr-1 h-3 w-3" /> Delete group
        </Button>
      </div>

      {busy ? (
        <p className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" /> Working...
        </p>
      ) : null}
      {msg ? (
        <p role={msg.ok ? 'status' : 'alert'} className={msg.ok ? 'text-success' : 'text-destructive'}>
          {msg.text}
        </p>
      ) : null}

      <div className="overflow-x-auto">
        <table className="w-full" data-testid="device-group-members">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="py-1.5 pr-3 font-normal">Device</th>
              <th className="py-1.5 pr-3 font-normal">OS</th>
              <th className="py-1.5 pr-3 font-normal">In the group by</th>
              <th className="py-1.5 pr-3 font-normal">Hours in force</th>
              <th className="py-1.5 pr-3 font-normal">From</th>
            </tr>
          </thead>
          <tbody>
            {(members ?? []).map((m) => (
              <tr key={m.podRowId} className="border-t border-border/30">
                <td className="py-1.5 pr-3">
                  <div className="text-foreground">{m.displayName}</div>
                  <div className="text-[11px] text-muted-foreground">{m.hostname ?? m.podId}</div>
                </td>
                <td className="py-1.5 pr-3">{m.os ?? '-'}</td>
                <td className="py-1.5 pr-3">{m.via === 'token' ? 'enrolment token' : m.via === 'rule' ? 'rule' : 'added'}</td>
                <td className="whitespace-nowrap py-1.5 pr-3 tabular-nums">
                  {shortWindow(m.effective.window)}
                  {m.reported && m.reported.window !== m.effective.window ? (
                    <div className="text-[11px] text-warning">reports {shortWindow(m.reported.window)}</div>
                  ) : null}
                </td>
                <td className="py-1.5 pr-3">{sourceText(m.effective, props.unitName(m.effective.businessUnitId))}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {members && members.length === 0 ? <p className="py-3 text-center text-muted-foreground">No devices in this group yet.</p> : null}
        {more ? (
          <div className="flex items-center gap-2 pt-2 text-[11px] text-muted-foreground">
            <span>
              Showing {count(members?.length ?? 0)} of {count(g.memberCount)} devices.
            </span>
            <Button size="sm" variant="ghost" onClick={() => void loadMore()}>
              Show more
            </Button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
